import React, { useState, useMemo } from 'react';
import { CardGrid, Spinner } from '@openedx/paragon';
import { FormattedMessage, useIntl } from '@edx/frontend-platform/i18n';

import { useCart } from '../../gql/hooks/useCart';
import messages from './messages';
import { useDiscoverCourses } from '../../gql/hooks/useDiscoverCourses';
import { useDiscoverPrograms } from '../../gql/hooks/useDiscoverPrograms';
import { useCourseTagsBatch } from '../../gql/hooks/useCourseTagsBatch';
import { useContentRatings } from '../../gql/hooks/useContentRatings';
import { useCoursesEffort } from '../discover/useCoursesEffort';
import { parseDiscoverCourses, parseDiscoverPrograms } from '../discover/courseDataParser';
import DiscoverCard from '../discover/components/DiscoverCard';
import { WishlistHeader, WishlistEmpty } from './components';

import './index.scss';

const Wishlist = () => {
  const { formatMessage } = useIntl();
  const [searchValue, setSearchValue] = useState('');

  const {
    coursesInWishlist,
    programsInWishlist,
    loading: cartLoading,
  } = useCart();

  const {
    discoverCourses,
    loading: coursesLoading,
    error: coursesError,
  } = useDiscoverCourses();
  const {
    discoverPrograms,
    loading: programsLoading,
    error: programsError,
  } = useDiscoverPrograms();

  const courseIds = useMemo(
    () => (discoverCourses || [])
      .map((c) => c?.openedxCourseId || c?.id)
      .filter(Boolean),
    [discoverCourses],
  );

  const contentIds = useMemo(() => {
    const ids = [];
    (discoverCourses || []).forEach((c) => c?.id && ids.push(c.id));
    (discoverPrograms || []).forEach((p) => p?.id && ids.push(p.id));
    return ids;
  }, [discoverCourses, discoverPrograms]);

  const { tagsByCourseId } = useCourseTagsBatch(courseIds);
  const { ratingsByContentId } = useContentRatings(contentIds);
  const { effortByCourseId } = useCoursesEffort(courseIds);

  const loading = cartLoading || coursesLoading || programsLoading;
  const error = coursesError || programsError;

  const wishlistedItems = useMemo(() => {
    const allCourses = parseDiscoverCourses(
      discoverCourses,
      tagsByCourseId,
      ratingsByContentId,
      effortByCourseId,
    );
    const allPrograms = parseDiscoverPrograms(discoverPrograms, ratingsByContentId);

    const courses = allCourses.filter(
      (item) => item.contentId && coursesInWishlist.has(item.contentId),
    );
    const programs = allPrograms.filter(
      (item) => item.contentId && programsInWishlist.has(item.contentId),
    );

    return [...programs, ...courses];
  }, [
    discoverCourses,
    discoverPrograms,
    tagsByCourseId,
    ratingsByContentId,
    effortByCourseId,
    coursesInWishlist,
    programsInWishlist,
  ]);

  const filteredItems = useMemo(() => {
    if (!searchValue.trim()) return wishlistedItems;
    const query = searchValue.toLowerCase();
    return wishlistedItems.filter(
      (item) => item.title.toLowerCase().includes(query)
        || item.skills.some((skill) => skill.toLowerCase().includes(query)),
    );
  }, [wishlistedItems, searchValue]);

  return (
    <div className="wishlist-page">
      <WishlistHeader
        searchValue={searchValue}
        onSearchChange={setSearchValue}
      />

      {loading && (
        <div className="wishlist-page__state">
          <Spinner
            animation="border"
            screenReaderText={formatMessage(messages.loading)}
          />
        </div>
      )}

      {error && !loading && (
        <div className="wishlist-page__state wishlist-page__state--error">
          <FormattedMessage {...messages.loadError} />
        </div>
      )}

      {!loading && !error && filteredItems.length === 0 && !searchValue && (
        <WishlistEmpty />
      )}

      {!loading && !error && filteredItems.length === 0 && searchValue && (
        <div className="wishlist-page__state">
          <FormattedMessage {...messages.noSearchMatches} />
        </div>
      )}

      {!loading && !error && filteredItems.length > 0 && (
        <section className="wishlist-page__grid">
          <CardGrid columnSizes={{ sm: 12, md: 6, lg: 4 }}>
            {filteredItems.map((item) => (
              <DiscoverCard key={item.id} {...item} />
            ))}
          </CardGrid>
        </section>
      )}
    </div>
  );
};

export default Wishlist;
